import { Link } from "react-router-dom";

export interface PostCardData {
  id: string;
  slug: string;
  title: string;
  categorySlug: string;
  excerpt: string;
  cover: string;
  alt: string;
  author: string;
  date: string;
  readTime: number;
  heat: number;
  tags: string[];
}

interface PostCardProps {
  post: PostCardData;
  categoryName: string;
  variant?: "default" | "compact" | "row";
  index?: number;
}

function formatHeat(heat: number) {
  if (heat >= 10000) return `${(heat / 10000).toFixed(1)}万`;
  return `${heat}`;
}

export default function PostCard({ post, categoryName, variant = "default", index = 0 }: PostCardProps) {
  if (variant === "row") {
    return (
      <article className="group flex gap-3 md:gap-4 py-4 border-b border-background-200 last:border-b-0">
        {typeof index === "number" && (
          <span className="font-brand text-2xl md:text-3xl text-background-400 shrink-0 w-8 text-center leading-none pt-1">
            {index + 1}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <Link to={`/post/${post.slug}`} className="block">
            <h3 className="text-sm md:text-base font-bold leading-snug text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
              {post.title}
            </h3>
          </Link>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <Link
              to={`/category/${post.categorySlug}`}
              className="text-secondary-700 bg-secondary-50 px-2 py-0.5 rounded-md hover:bg-secondary-100 transition-colors whitespace-nowrap"
            >
              {categoryName}
            </Link>
            <span className="whitespace-nowrap">{post.date}</span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-fire-line text-primary-500" aria-hidden="true" />
              {formatHeat(post.heat)}
            </span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === "compact") {
    return (
      <article className="group flex gap-3 items-start">
        <Link to={`/post/${post.slug}`} className="shrink-0 w-24 h-[68px] md:w-28 md:h-20 rounded-lg overflow-hidden bg-background-200">
          <img
            src={post.cover}
            alt={post.alt}
            title={`${post.title} - 网曝吃瓜爆料`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </Link>
        <div className="min-w-0 flex-1">
          <Link to={`/post/${post.slug}`}>
            <h4 className="text-sm font-bold leading-snug text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
              {post.title}
            </h4>
          </Link>
          <p className="mt-1.5 text-xs text-foreground-500 flex items-center gap-2">
            <span className="whitespace-nowrap">{post.date}</span>
            <span className="text-background-400">·</span>
            <span className="whitespace-nowrap">{categoryName}</span>
          </p>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors h-full">
      <Link to={`/post/${post.slug}`} className="block w-full h-44 md:h-48 overflow-hidden bg-background-200">
        <img
          src={post.cover}
          alt={post.alt}
          title={`${post.title} - 网曝吃瓜爆料`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </Link>
      <div className="p-4 md:p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2">
          <Link
            to={`/category/${post.categorySlug}`}
            className="text-xs text-secondary-800 bg-secondary-100 px-2 py-0.5 rounded-md hover:bg-secondary-200 transition-colors whitespace-nowrap"
          >
            {categoryName}
          </Link>
          {post.heat > 6000 && (
            <span className="text-xs text-accent-950 bg-accent-400 px-2 py-0.5 rounded-md whitespace-nowrap">
              热帖
            </span>
          )}
        </div>
        <Link to={`/post/${post.slug}`} className="block">
          <h3 className="text-base font-bold leading-snug text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
            {post.title}
          </h3>
        </Link>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">{post.excerpt}</p>
        <div className="mt-3 pt-3 border-t border-background-200 flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-1 truncate">
            <i className="ri-user-3-line shrink-0" aria-hidden="true" />
            <span className="truncate">{post.author}</span>
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-fire-line text-primary-500" aria-hidden="true" />
            {formatHeat(post.heat)}
          </span>
        </div>
      </div>
    </article>
  );
}